import { supabase } from './supabase';

/** Pull a human-readable string from any error shape (Error, Supabase error
 *  object with .message, plain string, or unknown). Avoids the dreaded
 *  "[object Object]" that surfaces when a non-Error is passed to String(). */
export function errMsg(e: unknown): string {
  if (!e) return 'Unknown error';
  if (typeof e === 'string') return e;
  if (e instanceof Error) return e.message;
  if (typeof e === 'object') {
    const obj = e as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown };
    if (typeof obj.message === 'string' && obj.message.length > 0) return obj.message;
    if (typeof obj.details === 'string' && obj.details.length > 0) return obj.details;
    if (typeof obj.hint    === 'string' && obj.hint.length    > 0) return obj.hint;
    if (typeof obj.code    === 'string' && obj.code.length    > 0) return `Error ${obj.code}`;
    try { return JSON.stringify(e); } catch { /* fall through */ }
  }
  return String(e);
}

/**
 * Team / multi-user DB layer.
 *
 * Wraps the RPCs from migration-012 plus a couple of plain SELECTs.
 * Seat-cap enforcement happens server-side in `create_invitation` and
 * the `enforce_org_seat_cap` trigger; the client just renders the
 * counts and surfaces friendly errors.
 *
 * v0.1: invitations are NOT auto-emailed. The Settings → Team UI
 * shows the invite link for the owner to share via WhatsApp / SMS /
 * email manually. Once Resend is wired in, switch to auto-send.
 */

export type TeamRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface TeamMember {
  id: string;
  user_id: string;
  email: string | null;
  full_name: string | null;
  role: TeamRole;
  joined_at: string;
}

export interface PendingInvite {
  id: string;
  email: string;
  role: TeamRole;
  token: string;
  invited_by: string | null;
  invited_by_name: string | null;
  expires_at: string;
  created_at: string;
}

export interface SeatUsage {
  used: number;
  cap: number;
  pending: number;
  tier: 'tier1' | 'tier2' | 'tier3' | 'enterprise';
}

const TIER_CAP: Record<SeatUsage['tier'], number> = {
  tier1: 3,
  tier2: 10,
  tier3: 25,
  enterprise: 1000,
};

const TIER_LABEL: Record<SeatUsage['tier'], string> = {
  tier1: 'Solo (£19/mo)',
  tier2: 'Team (£29/mo)',
  tier3: 'Site (£39/mo)',
  enterprise: 'Enterprise',
};

export function tierLabel(tier: SeatUsage['tier']): string {
  return TIER_LABEL[tier];
}

export function nextTier(tier: SeatUsage['tier']): SeatUsage['tier'] | null {
  if (tier === 'tier1') return 'tier2';
  if (tier === 'tier2') return 'tier3';
  if (tier === 'tier3') return 'enterprise';
  return null;
}

/** Lists all current members of an organisation, joined to profiles. */
export async function listTeamMembers(orgId: string): Promise<TeamMember[]> {
  const { data, error } = await supabase
    .from('org_members')
    .select('id, user_id, role, joined_at, profiles:user_id ( email, full_name )')
    .eq('org_id', orgId)
    .order('joined_at', { ascending: true });
  if (error) throw error;

  type Row = {
    id: string;
    user_id: string;
    role: TeamRole;
    joined_at: string;
    profiles: { email: string | null; full_name: string | null } | null;
  };

  return ((data ?? []) as unknown as Row[]).map((row) => ({
    id: row.id,
    user_id: row.user_id,
    role: row.role,
    joined_at: row.joined_at,
    email: row.profiles?.email ?? null,
    full_name: row.profiles?.full_name ?? null,
  }));
}

/** Lists pending (unaccepted, unexpired) invitations for an organisation. */
export async function listPendingInvites(orgId: string): Promise<PendingInvite[]> {
  const { data, error } = await supabase
    .from('invitations')
    .select('id, email, role, token, invited_by, expires_at, created_at, profiles:invited_by ( full_name )')
    .eq('org_id', orgId)
    .is('accepted_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });
  if (error) throw error;

  type Row = {
    id: string;
    email: string;
    role: TeamRole;
    token: string;
    invited_by: string | null;
    expires_at: string;
    created_at: string;
    profiles: { full_name: string | null } | null;
  };

  return ((data ?? []) as unknown as Row[]).map((row) => ({
    id: row.id,
    email: row.email,
    role: row.role,
    token: row.token,
    invited_by: row.invited_by,
    invited_by_name: row.profiles?.full_name ?? null,
    expires_at: row.expires_at,
    created_at: row.created_at,
  }));
}

/** Computes the current seat usage for an org. */
export async function getSeatUsage(orgId: string): Promise<SeatUsage> {
  const [{ data: org, error: oErr }, members, pending] = await Promise.all([
    supabase
      .from('organisations')
      .select('subscription_tier')
      .eq('id', orgId)
      .single(),
    supabase
      .from('org_members')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId),
    supabase
      .from('invitations')
      .select('id', { count: 'exact', head: true })
      .eq('org_id', orgId)
      .is('accepted_at', null)
      .gt('expires_at', new Date().toISOString()),
  ]);

  if (oErr) throw oErr;
  if (members.error) throw members.error;
  if (pending.error) throw pending.error;

  const tier: SeatUsage['tier'] =
    (org?.subscription_tier as SeatUsage['tier']) ?? 'tier1';

  return {
    used: members.count ?? 0,
    pending: pending.count ?? 0,
    cap: TIER_CAP[tier],
    tier,
  };
}

/**
 * Creates an invitation, fires off the auto-email via the Edge Function,
 * and returns the shareable URL (still copyable as a fallback if the email
 * fails to deliver).
 */
export async function createInvitation(input: {
  orgId: string;
  email: string;
  role?: 'admin' | 'member' | 'viewer';
}): Promise<{ token: string; expires_at: string; url: string; emailSent: boolean }> {
  const { data, error } = await supabase.rpc('create_invitation', {
    p_org_id: input.orgId,
    p_email: input.email.trim().toLowerCase(),
    p_role: input.role ?? 'admin',
  });
  if (error) throw error;
  const row = (data as Array<{ token: string; expires_at: string; id?: string }> | null)?.[0];
  if (!row) throw new Error('Invitation could not be created.');
  const url = `${window.location.origin}/invite/${row.token}`;

  // Fire off the branded auto-email via the Edge Function. The "copy link"
  // button stays in the UI as a fallback, so if delivery fails the owner
  // can still manually share. We need the invitation's `id` to send — the
  // RPC returns token + expires_at only, so look up by token to get id.
  let emailSent = false;
  try {
    const { data: invRow } = await supabase
      .from('invitations')
      .select('id')
      .eq('token', row.token)
      .single();
    if (invRow?.id) {
      const { data: sendResult, error: sendErr } = await supabase.functions.invoke(
        'send-invite-email',
        {
          body: {
            invitationId: invRow.id,
            appBaseUrl: window.location.origin,
          },
        },
      );
      emailSent = !sendErr && !!(sendResult as { sent?: boolean } | null)?.sent;
      if (sendErr) {
        // Soft-fail: log to console but don't throw — copy-link UX still works.
        console.warn('[createInvitation] email send failed:', sendErr);
      }
    }
  } catch (e) {
    console.warn('[createInvitation] email send threw:', e);
  }

  return { token: row.token, expires_at: row.expires_at, url, emailSent };
}

/** Owner/admin revokes an outstanding invite. */
export async function revokeInvite(inviteId: string): Promise<void> {
  const { error } = await supabase
    .from('invitations')
    .delete()
    .eq('id', inviteId);
  if (error) throw error;
}

/** Owner/admin removes a teammate from the org. Their login still works. */
export async function removeMember(memberId: string): Promise<void> {
  const { error } = await supabase
    .from('org_members')
    .delete()
    .eq('id', memberId);
  if (error) throw error;
}

/**
 * Accept an invitation. The invitee must already be authenticated
 * with the email the invite was sent to. Returns the org info on
 * success so the UI can route them to the new org.
 */
export async function acceptInvitation(token: string): Promise<{
  org_id: string;
  org_name: string;
  role: TeamRole;
}> {
  const { data, error } = await supabase.rpc('accept_invitation', {
    p_token: token,
  });
  if (error) throw error;
  const row = (data as Array<{ org_id: string; org_name: string; role: TeamRole }> | null)?.[0];
  if (!row) throw new Error('Invitation could not be accepted.');
  return row;
}

/** Look up an invitation by token (without accepting it) — used on the
 *  /invite/:token landing page so the user sees who invited them and
 *  what email they need to sign in with. Public read via RLS-safe RPC. */
export async function peekInvitation(token: string): Promise<{
  org_name: string;
  email: string;
  expires_at: string;
} | null> {
  // The invitations table is admin-read only. To allow peek-by-token
  // for the landing page we'd add an RPC; for now, the user lands,
  // signs up with whatever email, and accept_invitation does the
  // email match. Returning null here means "ask user to sign up
  // first, then click Accept".
  void token;
  return null;
}
